<script setup lang="ts">
import { scorePassword } from '@/utils/general';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiPasswordMeterProps {
  text: string | undefined;
  title?: boolean;
}
const props = withDefaults(defineProps<CiPasswordMeterProps>(), {
  title: false,
});

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext } = useGettext();

/********************
 * REFS & VARS       *
 ********************/
interface StrengthStyle {
  bg: string;
  width: string;
  text: string;
}

const i18nGetPasswordLabel = computed(() => {
  const labels = [$gettext('Weak'), $gettext('Ok'), $gettext('Nice'), $gettext('Strong'), $gettext('Very Strong')];
  return labels[passwordStrength.value];
});

const passwordStrength = computed(() => {
  if (!props.text) {
    return 0;
  }
  return scorePassword(props.text);
});

const titleClass = computed(() => {
  return props.title ? 'password-meter--with-title' : '';
});

const strengthStyles = computed((): StrengthStyle => {
  const level = passwordStrength.value;
  const styles: StrengthStyle[] = [
    { bg: 'bg-danger', width: 'w-[5%]', text: 'text-danger' },
    { bg: 'bg-danger', width: 'w-[25%]', text: 'text-danger' },
    { bg: 'bg-warning', width: 'w-[50%]', text: 'text-warning' },
    { bg: 'bg-warning', width: 'w-[75%]', text: 'text-warning' },
    { bg: 'bg-primary', width: 'w-[100%]', text: 'text-primary' },
  ];
  return styles[level]!;
});
</script>

<template>
  <div
    :class="[
      strengthStyles.text,
      titleClass,
      { 'password-meter--no-input': !props.text || props.text?.length < 1 },
    ]"
    :title="i18nGetPasswordLabel"
    class="password-meter w-full max-w-[50px] cursor-help"
  >
    <div class="relative top-px h-[5px] overflow-hidden rounded-[3px] bg-gray-10">
      <div
        :class="[
          (!props.text || props.text?.length < 1) ? 'w-0' : strengthStyles.width,
          strengthStyles.bg,
        ]"
        class="transition-width duration-250 absolute inset-y-0 left-0 ease-in"
      />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.password-meter:before {
  @apply absolute right-full top-[-7px] mr-[10px] hidden w-auto whitespace-nowrap text-sm content-[attr(title)] text-inherit;
}

.password-meter--with-title:before {
  @apply block;
}

.password-meter--no-input:before {
  @apply hidden;
}
</style>
